/**
 * Entry point.
 *
 * Boot order:
 *   1. Mount login screen (initially hidden).
 *   2. Probe /api/auth/me → if logged in, init app shell. Else show login.
 *   3. Init: mount canvas, legend, drag-drop, context menu; wire toolbar;
 *      fetch maps; auto-select the first.
 */

import * as api from './api.js';
import { bootstrapSession, mountLoginScreen, performLogout, showLoginScreen } from './auth.js';
import { initTelemetry, markTelemetryAuthenticated, withSpan } from './telemetry.js';
import { mountCanvas } from './canvas.js';
import { mountContextMenu } from './contextMenu.js';
import { mountDragDrop } from './dragDrop.js';
import { mountInitiativePanel } from './initiativePanel.js';
import { mountLegend } from './legend.js';
import { openManageMenu } from './manageMenu.js';
import { openAddNpcModal, openAddPcModal, openDeleteMapModal, openNewMapModal, openRenameMapModal } from './modals.js';
import {
  activeEncounterToken,
  encounterNewRound,
  encounterNext,
  encounterPrev,
  endEncounter,
  getState,
  loadMap,
  resetExplored,
  setEditMode,
  setState,
  startEncounter,
  subscribe,
} from './state.js';
import type { AppState } from './types.js';

const loginRoot = requireElement<HTMLElement>('#login-root');
const appRoot = requireElement<HTMLElement>('#app');
const canvas = requireElement<HTMLCanvasElement>('#map-canvas');
const mapSelector = requireElement<HTMLSelectElement>('#map-selector');
const currentMapName = requireElement<HTMLElement>('#current-map-name');
const userEmail = requireElement<HTMLElement>('#user-email');
const btnAddPc = requireElement<HTMLButtonElement>('#btn-add-pc');
const btnAddNpc = requireElement<HTMLButtonElement>('#btn-add-npc');
const btnManage = requireElement<HTMLButtonElement>('#btn-manage');
const btnEditOverlay = requireElement<HTMLButtonElement>('#btn-edit-overlay');
const submodeControls = requireElement<HTMLElement>('#overlay-submode-controls');
const btnSubmodeWalls = requireElement<HTMLButtonElement>('#btn-submode-walls');
const btnSubmodeTerrain = requireElement<HTMLButtonElement>('#btn-submode-terrain');
const btnResetFog = requireElement<HTMLButtonElement>('#btn-reset-fog');
const btnLogout = requireElement<HTMLButtonElement>('#btn-logout');
const legendRoot = requireElement<HTMLElement>('#legend-root');
const initiativeRoot = requireElement<HTMLElement>('#initiative-root');
const btnStartEncounter = requireElement<HTMLButtonElement>('#btn-start-encounter');
const btnPrevTurn = requireElement<HTMLButtonElement>('#btn-prev-turn');
const btnNextTurn = requireElement<HTMLButtonElement>('#btn-next-turn');
const btnNewRound = requireElement<HTMLButtonElement>('#btn-new-round');
const btnEndEncounter = requireElement<HTMLButtonElement>('#btn-end-encounter');
const encounterControls = requireElement<HTMLElement>('#encounter-controls');

/** Lifted so toolbar handlers (Next/Prev/New Round) can pan the camera. */
let canvasController: ReturnType<typeof mountCanvas> | null = null;

mountLoginScreen(loginRoot, () => {
  void bootIntoApp();
});

void start();

async function start(): Promise<void> {
  // Telemetry init first, in parallel with session bootstrap, so the very first
  // /api/auth/me call already produces a trace. A failure here must never
  // block login — we swallow and continue.
  const telemetryReady = api.getTelemetryConfig()
    .then((cfg) => initTelemetry(cfg))
    .catch((err) => console.warn('Telemetry setup failed; continuing without it', err));

  try {
    await telemetryReady;
    await withSpan('app.bootstrap', async () => {
      const ok = await bootstrapSession();
      if (ok) {
        await bootIntoApp();
      } else {
        showLoginScreen();
      }
    });
  } catch (err) {
    console.error('Bootstrap failed', err);
    showLoginScreen();
  }
}

/**
 * Boot the post-login UI exactly once per page lifetime. A second call is a
 * no-op except for re-fetching maps (so logging back in still refreshes the
 * map list without stacking duplicate listeners on the toolbar / canvas).
 *
 * Without this guard, every successful login after a logout would attach
 * another set of click handlers to the toolbar buttons, causing things like
 * "Edit walls" to toggle twice per click and visually never change.
 */
let booted = false;

async function bootIntoApp(): Promise<void> {
  // A session now exists (fresh login or an existing one detected on load).
  // Flush any spans the telemetry layer buffered while unauthenticated.
  markTelemetryAuthenticated();

  appRoot.hidden = false;

  if (!booted) {
    booted = true;
    const cc = mountCanvas(canvas);
    canvasController = cc;
    const panToToken = (token: { grid_x: number; grid_y: number; is_placed: boolean }): void => {
      if (!token.is_placed) return; // unplaced token has no cell to pan to
      cc.centerOnCell(token.grid_x, token.grid_y);
    };
    const legendController = mountLegend(legendRoot, panToToken);
    const initiativeController = mountInitiativePanel(initiativeRoot, panToToken);
    mountContextMenu(canvas);
    mountDragDrop(canvas, { getCellPx: cc.getCellPx });

    const renderPanels = (s: AppState): void => {
      const encounter = s.currentMap?.encounter ?? null;
      const inEncounter = encounter !== null;
      legendRoot.hidden = inEncounter;
      initiativeRoot.hidden = !inEncounter;
      if (inEncounter) {
        initiativeController.render(encounter, s.tokens);
      } else {
        legendController.render(s.tokens);
      }
    };

    subscribe((s) => {
      cc.render(s);
      renderPanels(s);
      syncToolbar(s);
      syncMapSelector(s);
    });

    // Initial render — the rAF loop in state.ts only fires after a setState,
    // so kick off the first paint manually.
    const state = getState();
    cc.render(state);
    renderPanels(state);
    syncToolbar(state);
    syncMapSelector(state);

    wireToolbar();
  }

  try {
    const maps = await api.getMaps();
    setState({ maps });
    // No auto-load: the GM picks a map explicitly from the selector or via
    // Manage → New Map. The canvas shows "No map selected" until then.
  } catch (err) {
    console.error('Failed to load maps', err);
  }
}

function wireToolbar(): void {
  btnLogout.addEventListener('click', () => {
    void performLogout();
  });
  btnManage.addEventListener('click', () => {
    const current = getState().currentMap;
    openManageMenu(btnManage, {
      hasCurrentMap: current !== null,
      onNewMap: () => openNewMapModal(),
      onRenameMap: () => {
        const map = getState().currentMap;
        if (map === null) return;
        openRenameMapModal({ id: map.id, name: map.name });
      },
      onDeleteMap: () => {
        const map = getState().currentMap;
        if (map === null) return;
        openDeleteMapModal({ id: map.id, name: map.name });
      },
    });
  });
  btnAddPc.addEventListener('click', () => {
    const map = getState().currentMap;
    if (map === null) return;
    openAddPcModal(map.id);
  });
  btnAddNpc.addEventListener('click', () => {
    const map = getState().currentMap;
    if (map === null) return;
    openAddNpcModal(map.id);
  });
  btnEditOverlay.addEventListener('click', () => {
    const next = getState().editMode === 'overlay' ? 'tokens' : 'overlay';
    setEditMode(next);
  });
  btnSubmodeWalls.addEventListener('click', () => {
    setState({ overlaySubMode: 'walls' });
  });
  btnSubmodeTerrain.addEventListener('click', () => {
    setState({ overlaySubMode: 'terrain' });
  });
  btnResetFog.addEventListener('click', () => {
    if (getState().currentMap === null) return;
    if (!window.confirm('Reset all explored areas? This cannot be undone.')) return;
    resetExplored();
  });

  const panToActive = (): void => {
    const t = activeEncounterToken();
    // Unplaced tokens (e.g. NPCs not yet on the map) have no cell to pan to;
    // they still appear in the initiative panel, just no camera movement.
    if (t !== null && t.is_placed) canvasController?.centerOnCell(t.grid_x, t.grid_y);
  };
  btnStartEncounter.addEventListener('click', () => {
    startEncounter();
    panToActive();
  });
  btnNextTurn.addEventListener('click', () => {
    encounterNext();
    panToActive();
  });
  btnPrevTurn.addEventListener('click', () => {
    encounterPrev();
    panToActive();
  });
  btnNewRound.addEventListener('click', () => {
    encounterNewRound();
    panToActive();
  });
  btnEndEncounter.addEventListener('click', () => {
    endEncounter();
  });

  mapSelector.addEventListener('change', () => {
    const id = Number(mapSelector.value);
    if (Number.isFinite(id) && id > 0) {
      void loadMap(id);
    }
  });
}

function syncToolbar(state: AppState): void {
  userEmail.textContent = state.user?.email ?? '';
  currentMapName.textContent = state.currentMap?.name ?? '';
  const hasMap = state.currentMap !== null;
  const encounter = state.currentMap?.encounter ?? null;
  const inEncounter = encounter !== null;
  btnAddPc.disabled = !hasMap;
  btnAddNpc.disabled = !hasMap;
  btnEditOverlay.disabled = !hasMap;

  const editing = state.editMode === 'overlay';
  btnEditOverlay.setAttribute('aria-pressed', editing ? 'true' : 'false');
  btnEditOverlay.textContent = editing ? 'Done editing' : 'Edit map';
  document.body.classList.toggle('mode-overlay', editing);

  submodeControls.hidden = !editing;
  const subMode = state.overlaySubMode;
  btnSubmodeWalls.setAttribute('aria-pressed', subMode === 'walls' ? 'true' : 'false');
  btnSubmodeTerrain.setAttribute('aria-pressed', subMode === 'terrain' ? 'true' : 'false');

  // Encounter controls: Start shown when a map is loaded & no encounter;
  // the step controls shown only while an encounter is active. The round
  // number is shown in the initiative panel header, not the toolbar.
  btnStartEncounter.hidden = inEncounter;
  btnStartEncounter.disabled = !hasMap || inEncounter;
  encounterControls.hidden = !inEncounter;
}

function syncMapSelector(state: AppState): void {
  const currentId = state.currentMap?.id ?? '';
  mapSelector.innerHTML = '';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = '— No map —';
  mapSelector.appendChild(placeholder);
  for (const m of state.maps) {
    const opt = document.createElement('option');
    opt.value = String(m.id);
    opt.textContent = m.name;
    if (m.id === currentId) opt.selected = true;
    mapSelector.appendChild(opt);
  }
}

function requireElement<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (el === null) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return el;
}

(window as unknown as { __mapnmoveDebug?: unknown }).__mapnmoveDebug = { getState };
